import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { setDialog, setPageName } from '../../../../app/appSlice';
import { fetchCategoriesAsync, selectCategories } from '../CategoriesView/categorySlice';
import { useParams } from 'react-router-dom';
import { destroyQuestionAsync, fetchQuestionsAsync, selectQuestions } from './questionSlice';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColumns, GridRowParams } from '@mui/x-data-grid';
import QuestionDialog from './QuestionDialog';
import { Question } from '../../../../generated/api';
import { Delete } from '@mui/icons-material';
import { DialogState } from '../../../../app/types';

const QuestionsView = () => {
  const { categoryId } = useParams();
  const dispatch = useAppDispatch();
  const categoryState = useAppSelector(selectCategories);
  const questionState = useAppSelector(selectQuestions);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);

  const category = categoryState.categories.find((c) => String(c.id) === categoryId);

  useEffect(() => {
    dispatch(setPageName('Questions'));
    if (!category) {
      dispatch(fetchCategoriesAsync());
    }
  }, []);

  useEffect(() => {
    if (category) {
      dispatch(fetchQuestionsAsync(category.id));
    }
  }, [category]);

  const deleteQuestion = (question: Question) => () => {
    const dialog: DialogState = {
      open: true,
      title: `Are you sure you want to delete question "${question.name}"?`,
      description: 'This action cannot be undone.',
      onOk: () => dispatch(destroyQuestionAsync(question.id))
    };
    dispatch(setDialog(dialog));
  };

  const columns = useMemo<GridColumns<Question>>(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, headerAlign: 'center', align: 'center' },
      { field: 'description', headerName: 'Description', flex: 3, headerAlign: 'center', align: 'center' },
      { field: 'sortIndex', headerName: 'Sort Index', maxWidth: 100, headerAlign: 'center', align: 'center' },
      {
        field: 'actions',
        type: 'actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem key={1} label="delete" icon={<Delete />} onClick={deleteQuestion(params.row)} />
        ]
      }
    ],
    [deleteQuestion]
  );

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedQuestion(null);
    if (!category) return;
    dispatch(fetchQuestionsAsync(category.id));
  };

  if (!category) return null;
  return (
    <Box marginTop={1} minWidth="100%">
      <Container maxWidth="lg">
        <Paper>
          <Typography variant="h5" align="center">
            {category.name}
          </Typography>
          <Typography align="center">{category.description}</Typography>
          <Button variant="contained" onClick={() => setModalOpen(true)}>
            New Question
          </Button>
          <DataGrid
            autoHeight
            columns={columns}
            rows={questionState.questions}
            onRowDoubleClick={({ row }) => {
              setSelectedQuestion(row);
              setModalOpen(true);
            }}
          />
        </Paper>
        <QuestionDialog open={modalOpen} close={handleCloseModal} category={category} question={selectedQuestion} />
      </Container>
    </Box>
  );
};

export default QuestionsView;
